import { useEffect, useState } from "react";
import { ApiClientError } from "@podecontratar/shared/contract/client-error";
import type { EndpointOutput } from "@podecontratar/shared/contract/endpoint";
import type { ApiContract } from "@podecontratar/shared/contract/index";
import { api } from "./lib/api";
import { websiteOrigin } from "./lib/website-origin";
import type { AdminRole } from "@podecontratar/shared/lib/admin-permissions";
import { AdminRoleProvider } from "./AdminRoleContext";

/**
 * Client-side auth gate for the admin SPA.
 *
 * Vite/Pages has no server-component hook, so the check runs on the
 * client: a single `/api/auth/me` probe at boot, gated on the `role`
 * the API resolves from D1.
 *
 * Failure paths:
 *   - 401 / not authenticated → bounce to login on the website
 *   - 200 but no admin role → render a permission-denied card
 *   - 200 + a role → provide it to the tree and render the admin app
 *
 * The auth cookie is set on `.podecontratar.com`, so a login on the
 * marketing site carries here without a separate sign-in form.
 */

type Me = EndpointOutput<ApiContract["GET /auth/me"]>;


export function AuthGate({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; user: Me }
    | { kind: "unauthorized"; email: string }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const user = await api.request("GET /auth/me");
        if (cancelled) return;
        if (!user.role) {
          setState({ kind: "unauthorized", email: user.email });
          return;
        }
        setState({ kind: "ok", user });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === "unauthorized") {
          // Send the user to the website's login page; on success they
          // land back on admin.podecontratar.com.
          const redir = encodeURIComponent(window.location.href);
          window.location.href = `${websiteOrigin()}/auth/login?redir=${redir}`;
          return;
        }
        setState({
          kind: "error",
          message:
            err instanceof ApiClientError && err.code === "forbidden"
              ? "Esta conta não está ativa."
              : "Erro ao validar acesso.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === "loading") {
    return (
      <div className="grid min-h-screen place-items-center bg-surface text-text-secondary">
        <p className="text-sm tracking-tight">Validando acesso…</p>
      </div>
    );
  }

  if (state.kind === "unauthorized") {
    return (
      <PermissionDenied email={state.email} />
    );
  }

  if (state.kind === "error") {
    return (
      <div className="grid min-h-screen place-items-center bg-surface px-6 text-center">
        <div className="max-w-md">
          <h1 className="text-xl font-semibold tracking-tight text-text">
            Não consegui validar sua sessão.
          </h1>
          <p className="mt-3 text-sm text-text-secondary">{state.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex items-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface-alt"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminRoleProvider role={state.user.role as AdminRole}>{children}</AdminRoleProvider>
  );
}

function PermissionDenied({ email }: { email: string }) {
  return (
    <div className="grid min-h-screen place-items-center bg-surface px-6 text-center">
      <div className="max-w-md">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-text-tertiary">
          Acesso restrito
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight text-text">
          Você não tem acesso a este painel.
        </h1>
        <p className="mt-4 text-sm text-text-secondary">
          Você está logado(a) como <code className="font-mono text-xs">{email}</code>.
          Se precisa de acesso, peça a quem administra o sistema.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <a
            href={`${websiteOrigin()}/painel`}
            className="inline-flex items-center rounded-lg bg-accent px-4 py-2 text-sm font-medium text-text-inverse hover:bg-accent-hover"
          >
            Voltar pro painel
          </a>
          <a
            href={`${websiteOrigin()}/auth/logout`}
            className="inline-flex items-center rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium hover:bg-surface-alt"
          >
            Sair e trocar de conta
          </a>
        </div>
      </div>
    </div>
  );
}
